import { createFileRoute, Link } from "@tanstack/react-router";
import { absoluteUrl } from "@/lib/site";
import { useEffect } from "react";
import { PILLARS } from "@/lib/method";
import { track } from "@/lib/analytics";

export const Route = createFileRoute("/method/")({
  head: () => ({
    meta: [
      { title: "The KAfrequency Method — Ten Pillars of Practice" },
      { name: "description", content: "The organizing framework of KAfrequency: ten pillars — Presence, Attention, Discipline, Breath, Consciousness, Creativity, Music, Wisdom, Purpose, Integration — woven through every song, teaching, activation, and book." },
      { property: "og:title", content: "The KAfrequency Method" },
      { property: "og:description", content: "Ten pillars that organize music, wisdom, and practice into one coherent path." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://buddhaka.com/method" },
      { property: "og:site_name", content: "Buddha KA · KAfrequency" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "The KAfrequency Method" },
      { name: "twitter:description", content: "Ten pillars. One current." },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/method") }],
  }),
  component: MethodOverview,
});

function MethodOverview() {
  useEffect(() => { track("method_view"); }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
      <header className="mx-auto max-w-3xl text-center">
        <p className="text-[10px] uppercase tracking-[0.4em] text-gold">The Method</p>
        <h1 className="mt-4 font-serif text-4xl leading-tight sm:text-6xl">
          Ten pillars.<br/><span className="text-gold-luminous">One current.</span>
        </h1>
        <p className="mt-6 text-base text-muted-foreground sm:text-lg">
          KAfrequency is not a genre or a technique. It is a way of moving through the world —
          where music becomes instruction, breath becomes architecture, and attention becomes the
          currency you refuse to spend carelessly. The Method organizes every song, teaching,
          activation, and book into ten living pillars.
        </p>
      </header>

      <ol className="mt-20 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {PILLARS.map((p, i) => (
          <li key={p.id}>
            <Link
              to="/method/$pillar"
              params={{ pillar: p.id }}
              className="group relative flex h-full flex-col gap-3 rounded-2xl border hairline bg-obsidian/60 p-6 backdrop-blur transition-all hover:border-gold hover:bg-obsidian/80"
            >
              <div className="flex items-baseline justify-between">
                <span className="text-[10px] uppercase tracking-[0.3em] text-gold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span aria-hidden className="font-serif text-2xl text-gold-luminous">{p.glyph}</span>
              </div>
              <h2 className="font-serif text-2xl text-ivory group-hover:text-gold-luminous">
                {p.name}
              </h2>
              <p className="text-sm text-gold/80">{p.essence}</p>
              <p className="mt-auto pt-3 text-sm text-muted-foreground">{p.invitation}</p>
            </Link>
          </li>
        ))}
      </ol>

      <section className="mt-24 rounded-2xl border hairline bg-obsidian/40 p-8 sm:p-12 text-center">
        <p className="text-[10px] uppercase tracking-[0.4em] text-gold">How to use the Method</p>
        <h2 className="mt-3 font-serif text-3xl">Enter through any gate.</h2>
        <p className="mx-auto mt-4 max-w-2xl text-sm text-muted-foreground">
          There is no correct sequence. Choose the pillar that calls you loudest today —
          the Method arranges music, teachings, and activations around whichever thread you
          follow. The current will bring you to the rest in its own time.
        </p>
      </section>
    </div>
  );
}
